import apiClient from './apiClient';

/**
 * Interface definitions for API Key management (adapted to existing backend)
 */
export interface Firm {
  Firm_ID: string;
  name: string;
  abbrev: string;
}

export interface APIKey {
  APIKey_ID: string;
  Key: string; // Masked key
  firms: Firm[];
  role: string[];
  description: string;
  created_at?: string;
  updated_at?: string;
  org?: string; // Deprecated, legacy field
}

export interface APIKeyCreateRequest {
  firm_ids: string[];
  role: string[];
  description: string;
}

export interface APIKeyCreateResponse {
  APIKey_ID: string;
  Key: string; // Full key - only shown once
  Secret: string; // Full secret - only shown once
  firms: Firm[];
  role: string[];
  description: string;
  created_at?: string;
}

export interface Role {
  id: string;
  name: string;
  description: string;
}

export interface RolesResponse {
  roles: Role[];
}

/**
 * API Key management functions
 */
export const apiKeyApi = {
  /**
   * Get all API keys
   */
  getAllApiKeys: async (): Promise<APIKey[]> => {
    try {
      console.log('Fetching API keys from API...');
      const response = await apiClient.get<APIKey[]>('/api/v2/web/apikeys');
      console.log('API keys response data:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error in getAllApiKeys:', error);
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to access API key management. Admin or API Admin role required.');
      }
      throw error;
    }
  },

  /**
   * Get a single API key by ID
   */
  getApiKeyById: async (keyId: string): Promise<APIKey> => {
    try {
      const response = await apiClient.get<APIKey>(`/api/v2/web/apikeys/${keyId}`);
      return response.data;
    } catch (error: any) {
      console.error('Error in getApiKeyById:', error);
      if (error?.response?.status === 404) {
        throw new Error('API key not found');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to access this API key');
      }
      throw error;
    }
  },

  /**
   * Create a new API key
   */
  createApiKey: async (keyData: APIKeyCreateRequest): Promise<APIKeyCreateResponse> => {
    try {
      console.log('Creating API key:', keyData);
      const response = await apiClient.post<APIKeyCreateResponse>('/api/v2/web/apikeys', keyData);
      console.log('API key created successfully:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error in createApiKey:', error);
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to create API keys. Admin or API Admin role required.');
      }
      if (error?.response?.status === 400) {
        throw new Error('Invalid API key data provided');
      }
      throw error;
    }
  },

  /**
   * Delete an API key
   */
  deleteApiKey: async (keyId: string): Promise<void> => {
    try {
      console.log('Deleting API key:', keyId);
      await apiClient.delete(`/api/v2/web/apikeys/${keyId}`);
      console.log('API key deleted successfully');
    } catch (error: any) {
      console.error('Error in deleteApiKey:', error);
      if (error?.response?.status === 404) {
        throw new Error('API key not found');
      }
      if (error?.response?.status === 403) {
        throw new Error('You do not have permission to delete API keys. Admin or API Admin role required.');
      }
      throw error;
    }
  }
};
